import { supabase as _supabaseInstance } from '../config/supabase.js';

const getSupabase = () => _supabaseInstance;

async function callAdminRpc(action, params) {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('admin_manage_user', {
        p_action: action,
        ...params,
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Browser Cache Layer  (Redis-equivalent for a static app)
//
//  • _memCache  — in-memory JS object.  Zero latency, cleared on tab close.
//  • localStorage — persists across page refreshes for instant warm-start.
//  • TTL 5 minutes — after that a fresh Supabase fetch is forced.
//  • Any write (update / delete / upload) calls cacheInvalidate('leads')
//    so the next read always gets fresh data.
// ─────────────────────────────────────────────────────────────────────────────
const CACHE_TTL_MS = 15 * 60 * 1000;       // 15 min — return immediately
const CACHE_STALE_TTL_MS = 60 * 60 * 1000; // 60 min — return stale + refresh in bg
// _v2: the previous key could hold a poisoned empty-array entry that survived
// refreshes and made every admin view show zero leads. Bumping the key abandons it.
const LS_CACHE_KEY = 'crm_leads_cache_v2';
const _memCache = {};
const _bgRefreshing = new Set(); // prevent duplicate background fetches

// Columns for list queries — excludes heavy JSON blobs (notes, activities, tasks,
// buyorder_data, task_notifications) that bloat responses beyond Supabase's payload
// limit when fetching thousands of rows.  Full data is fetched via getLeadById().
// Never select * from crm_users: the row carries a legacy `password` column, and
// getUsers() is called on page load by agent views, which shipped every user's
// password to every browser. Logins go through Supabase Auth, so nothing here
// needs it.
export const CRM_USER_COLUMNS = 'id,username,role,first_name,last_name,full_name,email,phone,agent_group,is_active,status,last_login,created_at,updated_at,team_id,auth_user_id';

const LEADS_LIST_COLUMNS ='id,firstname,lastname,primaryemail,primaryphone,secondaryemail,secondaryphone,country,city,address,postcode,leadsource,status,vc,tqd,group_name,assignedto,user_id,description,buyorder_submitted,buyorder_status,buyorder_date,admin_notes,deal_stage,deal_created_date,callback_date,callback_time,callback_set_at,createdat,updatedat,click_count,last_viewed_at,last_viewed_by,assigned_to,created_at,updated_at,completed_at,completed_by,sent_back_at,sent_back_by,was_fronted,task_status,callback_notes,front_date,front_time,task_completed,task_completed_at,task_completed_by,team_id';

// An empty array is never a valid cached result — it is far more likely to be a
// failed or truncated fetch than a genuinely empty table, and caching it hides
// every record from the UI until the entry expires. Treat it as a miss.
const isUsableCache = (data) => Array.isArray(data) && data.length > 0;

function cacheGet(key) {
    // 1. Check in-memory first (fastest)
    const mem = _memCache[key];
    if (mem && (Date.now() - mem.ts) < CACHE_TTL_MS && isUsableCache(mem.data)) {
        console.log(`⚡ Cache HIT (memory): ${key} — ${mem.data.length} items`);
        return mem.data;
    }
    // 2. Fall back to localStorage (survives page refresh)
    try {
        const raw = localStorage.getItem(LS_CACHE_KEY + '_' + key);
        if (raw) {
            const { ts, data } = JSON.parse(raw);
            if ((Date.now() - ts) < CACHE_TTL_MS && isUsableCache(data)) {
                _memCache[key] = { ts, data };
                console.log(`⚡ Cache HIT (localStorage): ${key} — ${data.length} items`);
                return data;
            }
        }
    } catch (e) { /* ignore parse errors */ }
    return null;
}

// Returns stale cache data (up to 60 min old) — used for instant display while bg refresh runs
function cacheGetStale(key) {
    const mem = _memCache[key];
    if (mem && (Date.now() - mem.ts) < CACHE_STALE_TTL_MS && isUsableCache(mem.data)) return mem.data;
    try {
        const raw = localStorage.getItem(LS_CACHE_KEY + '_' + key);
        if (raw) {
            const { ts, data } = JSON.parse(raw);
            if ((Date.now() - ts) < CACHE_STALE_TTL_MS && isUsableCache(data)) {
                _memCache[key] = { ts, data };
                return data;
            }
        }
    } catch (e) {}
    return null;
}

function cacheSet(key, data) {
    // Never store an empty result: see isUsableCache above.
    if (Array.isArray(data) && data.length === 0) {
        console.warn(`⚠️ Refusing to cache an empty result for ${key}`);
        cacheInvalidate(key);
        return;
    }
    const ts = Date.now();
    _memCache[key] = { ts, data };
    try {
        // Only cache arrays ≤ 50k items to avoid hitting localStorage quota
        if (Array.isArray(data) && data.length <= 50000) {
            localStorage.setItem(LS_CACHE_KEY + '_' + key, JSON.stringify({ ts, data }));
        }
    } catch (e) { /* quota exceeded — memory cache still works */ }
    console.log(`💾 Cache SET: ${key} — ${Array.isArray(data) ? data.length : 1} items`);
}

// The app authenticates with its own session rather than Supabase Auth, so the
// database cannot identify the acting user on its own. Every write stamps
// updated_by from the stored session; the audit triggers read it.
function currentUsername() {
    for (const key of ['crm_session', 'crm_user']) {
        try {
            const raw = localStorage.getItem(key);
            if (raw) {
                const username = JSON.parse(raw)?.username;
                if (username) return username;
            }
        } catch (e) { /* unreadable session — fall through */ }
    }
    return null;
}

// Row-level security on `leads` only grants access to the `authenticated` role.
// If the Supabase session has lapsed the client silently falls back to `anon`,
// and every query returns zero rows with no error — which looks identical to an
// empty CRM. Fail loudly instead so the UI can send the user back to sign in.
export class SessionExpiredError extends Error {
    constructor() {
        super('Your session has expired. Please sign in again.');
        this.name = 'SessionExpiredError';
    }
}

// RLS hides rows the signed-in user is not allowed to read, and a hidden row is
// indistinguishable from a deleted one: both come back as zero rows.  Callers
// must never fall back to the slim list row when this is thrown — that row
// carries notes/tasks/activities as [], so the modal would look empty and a
// later save would write those blanks over the real data.
export class LeadNotVisibleError extends Error {
    constructor(id) {
        super(`Lead ${id} could not be loaded — your session may have expired, or the lead is no longer assigned to you. Sign out, sign in again, and reopen it. No data has been lost.`);
        this.name = 'LeadNotVisibleError';
    }
}

async function assertSignedIn() {
    try {
        const { data } = await getSupabase().auth.getSession();
        if (!data?.session) throw new SessionExpiredError();
    } catch (e) {
        if (e instanceof SessionExpiredError) throw e;
        // Could not determine session state — let the query proceed rather than
        // blocking work on a transient auth-storage read failure.
    }
}

function cacheInvalidate(key) {
    delete _memCache[key];
    try { localStorage.removeItem(LS_CACHE_KEY + '_' + key); } catch (e) {}
    console.log(`🗑️ Cache INVALIDATED: ${key}`);
}

// Patch a single record in the cache without wiping the whole thing.
// Called after updateLead so a save doesn't force a full re-fetch.
function cachePatch(key, record) {
    const existing = cacheGet(key);
    if (!existing) return; // nothing cached — skip
    const idx = existing.findIndex(r => r.id === record.id);
    if (idx === -1) {
        // New record not yet in cache — append it
        cacheSet(key, [...existing, record]);
        return;
    }
    const updated = [...existing];
    updated[idx] = { ...updated[idx], ...record };
    cacheSet(key, updated);
    console.log(`🔧 Cache PATCHED: ${key} record ${record.id}`);
}

// A note is identified by who wrote it and when.  Editing a note keeps both, so
// an edit is not mistaken for a delete, and two notes written in the same
// millisecond by the same person are vanishingly unlikely.
function noteIdentity(note) {
    return `${note?.createdAt || ''}|${note?.createdBy || ''}`;
}

// Every modal handler builds its payload by spreading a lead object it captured
// when that modal opened — selectedLeadForTask, selectedLeadForBuyOrder and the
// rest are separate copies that adding a note does not update.  Scheduling a
// callback seconds after writing a note therefore shipped the pre-note array and
// silently destroyed the note.  Re-read the row and put back any note the
// payload would drop: writes may add and edit notes, but only an explicit
// delete may remove one.
async function guardNotes(supabase, id, cleanedData) {
    if (!('notes' in cleanedData)) return;

    const { data: current, error } = await supabase
        .from('leads').select('notes').eq('id', id).maybeSingle();
    if (error) throw error;
    if (!current) throw new LeadNotVisibleError(id);

    const existing = Array.isArray(current.notes) ? current.notes : [];
    const incoming = cleanedData.notes;

    // A payload that is not an array at all (a slim list row carries notes as
    // null) must never reach the column.
    if (!Array.isArray(incoming)) {
        delete cleanedData.notes;
        if (existing.length > 0) {
            console.warn(`⚠️ Dropped a non-array notes payload for lead ${id} — kept the ${existing.length} stored notes.`);
        }
        return;
    }

    const kept = new Set(incoming.map(noteIdentity));
    const rescued = existing.filter(note => !kept.has(noteIdentity(note)));
    if (rescued.length === 0) return;

    console.warn(`⚠️ Write to lead ${id} would have dropped ${rescued.length} note(s) written since this screen loaded — merging them back.`);
    cleanedData.notes = [...incoming, ...rescued].sort((a, b) =>
        String(a?.createdAt || '').localeCompare(String(b?.createdAt || ''))
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Supabase CRM Service
class CRMService {
    // Leads
    async uploadLeads(leadsData) {
        const supabase = getSupabase();

        // Insert one by one to avoid batch failures
        let successCount = 0;
        const errors = [];

        console.log(`📤 Starting upload of ${leadsData.length} leads...`);

        for (let i = 0; i < leadsData.length; i++) {
            const lead = leadsData[i];
            try {
                const { error } = await supabase
                    .from('leads')
                    .insert(lead);

                if (error) {
                    console.error(`Row ${i + 1} failed:`, error.message, lead);
                    errors.push({ row: i + 1, error: error.message, name: `${lead.firstname} ${lead.lastname}` });
                } else {
                    successCount++;
                    if ((i + 1) % 100 === 0) {
                        console.log(`  Uploaded ${i + 1}/${leadsData.length} leads...`);
                    }
                }
            } catch (err) {
                console.error(`Row ${i + 1} exception:`, err.message);
                errors.push({ row: i + 1, error: err.message, name: `${lead.firstname} ${lead.lastname}` });
            }
        }

        if (errors.length > 0) {
            console.warn(`Upload completed with ${errors.length} errors:`, errors);
        }

        console.log(`✅ Upload complete: ${successCount} succeeded, ${errors.length} failed`);
        if (successCount > 0) cacheInvalidate('leads');
        return { successCount, errors };
    }

    /**
     * Get leads with optional server-side filtering and cursor-based pagination
     * @param {Object} filters - Optional filters to apply server-side
     * @param {string} filters.status - Filter by status
     * @param {string} filters.country - Filter by country
     * @param {string} filters.vc - Filter by VC
     * @param {string} filters.leadsource - Filter by lead source
     * @param {string} filters.assignedto - Filter by assigned agent
     * @param {number} filters.lastId - Cursor for pagination (fetch leads with id > lastId)
     * @param {number} filters.limit - Limit number of results (default: fetch all)
     */
    async getLeads(filters = {}) {
        const supabase = getSupabase();

        // If no filters provided, use the original batch fetch approach for backwards compatibility
        if (Object.keys(filters).length === 0) {
            return this._getLeadsLegacy(filters._skipCache);
        }

        await assertSignedIn();

        // Exclude heavy JSON columns from list queries to stay within response size limits.
        // Full details (notes, activities, tasks, etc.) are fetched via getLeadById().
        let query = supabase.from('leads').select(LEADS_LIST_COLUMNS).eq('archived', false);

        // Apply filters server-side (database does the filtering, not JavaScript)
        if (filters.status && filters.status !== 'all') {
            query = query.eq('status', filters.status);
        }
        if (filters.country && filters.country !== 'all') {
            query = query.eq('country', filters.country);
        }
        if (filters.vc && filters.vc !== 'all') {
            query = query.eq('vc', filters.vc);
        }
        if (filters.leadsource && filters.leadsource !== 'all') {
            query = query.eq('leadsource', filters.leadsource);
        }
        if (filters.assignedto) {
            // Case-insensitive OR match — handles case mismatches and multiple identifiers
            // (username vs full name). Wraps values in quotes so spaces work in PostgREST.
            const values = Array.isArray(filters.assignedto) ? filters.assignedto : [filters.assignedto];
            const orFilter = values
                .map(v => `assignedto.ilike."${String(v).replace(/"/g, '')}"`)
                .join(',');
            query = query.or(orFilter);
        }

        // Always order by id for consistent cursor pagination
        query = query.order('id', { ascending: true });

        // If a specific limit is requested (non-agent use), honour it and return single batch
        if (filters.limit) {
            if (filters.lastId) query = query.gt('id', filters.lastId);
            query = query.limit(filters.limit);
            const { data, error } = await query;
            if (error) { console.error('Error fetching leads with filters:', error); throw error; }
            console.log(`📊 getLeads (filtered) returned ${data?.length || 0} leads`);
            return data || [];
        }

        // No explicit limit — use cursor-based pagination to fetch ALL matching leads
        // (Supabase caps single queries at 1000 rows; agents can have thousands of leads)
        const batchSize = 500;
        let allLeads = [];
        let lastId = filters.lastId || 0;

        while (true) {
            let batchQuery = query;
            if (lastId > 0) batchQuery = batchQuery.gt('id', lastId);
            batchQuery = batchQuery.limit(batchSize);

            const { data, error } = await batchQuery;
            if (error) { console.error('Error fetching leads with filters:', error); throw error; }
            if (!data || data.length === 0) break;

            allLeads = allLeads.concat(data);
            if (data.length < batchSize) break;
            lastId = data[data.length - 1].id;
        }

        console.log(`📊 getLeads (filtered) returned ${allLeads.length} leads`);
        return allLeads;
    }

    // Fetch all leads in batches (background, updates cache when done)
    async _fetchAllLeadsIntoCache() {
        if (_bgRefreshing.has('leads')) return; // already running
        _bgRefreshing.add('leads');
        try {
            const supabase = getSupabase();
            let allLeads = [];
            let lastId = 0;
            const batchSize = 1000;
            while (true) {
                let query = supabase.from('leads').select(LEADS_LIST_COLUMNS).eq('archived', false).order('id', { ascending: true }).limit(batchSize);
                if (lastId > 0) query = query.gt('id', lastId);
                const { data, error } = await query;
                if (error) throw error;
                if (!data || data.length === 0) break;
                allLeads = allLeads.concat(data);
                if (data.length < batchSize) break;
                lastId = data[data.length - 1].id;
            }
            cacheSet('leads', allLeads);
            console.log(`🔄 Background refresh complete: ${allLeads.length} leads`);
        } catch (e) {
            console.error('Background leads refresh failed:', e);
        } finally {
            _bgRefreshing.delete('leads');
        }
    }

    // Legacy method for backwards compatibility - fetches all leads in batches
    async _getLeadsLegacy(skipCache = false) {
        await assertSignedIn();
        // ── Cache check ──────────────────────────────────────────────────────
        if (!skipCache) {
            const cached = cacheGet('leads');
            if (cached && cached.length > 0) return cached;

            // Stale-while-revalidate: return slightly old data instantly,
            // kick off a background refresh so next poll gets fresh data
            const stale = cacheGetStale('leads');
            if (stale && stale.length > 0) {
                console.log(`⏳ Stale cache hit — returning ${stale.length} leads instantly, refreshing in background`);
                this._fetchAllLeadsIntoCache(); // fire-and-forget
                return stale;
            }
        }
        // ────────────────────────────────────────────────────────────────────

        const supabase = getSupabase();

        // Cursor-based pagination (more efficient than offset-based)
        let allLeads = [];
        let lastId = 0;
        const batchSize = 1000;
        const maxRetries = 3;

        console.log('📥 Starting to fetch leads (cursor-based)...');

        while (true) {
            let retryCount = 0;
            let batchSuccess = false;
            let batchData = null;

            while (retryCount < maxRetries && !batchSuccess) {
                try {
                    // Cursor-based: WHERE id > lastId instead of OFFSET
                    // This is O(1) instead of O(n) for large datasets
                    let query = supabase
                        .from('leads')
                        .select(LEADS_LIST_COLUMNS)
                        .eq('archived', false)
                        .order('id', { ascending: true })
                        .limit(batchSize);

                    if (lastId > 0) {
                        query = query.gt('id', lastId);
                    }

                    const { data, error } = await query;

                    if (error) {
                        console.error(`Supabase error (attempt ${retryCount + 1}):`, error);

                        if (error.message && error.message.includes('statement timeout')) {
                            retryCount++;
                            if (retryCount < maxRetries) {
                                await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
                                continue;
                            }
                            throw new Error(`Database timeout after ${maxRetries} attempts.`);
                        }
                        throw error;
                    }

                    batchData = data;
                    batchSuccess = true;

                } catch (err) {
                    if (retryCount >= maxRetries - 1) throw err;
                    retryCount++;
                    await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
                }
            }

            if (!batchData || batchData.length === 0) {
                console.log('  Finished fetching all leads');
                break;
            }

            console.log(`  Fetched ${batchData.length} leads (total: ${allLeads.length + batchData.length})`);
            allLeads = allLeads.concat(batchData);

            // Update cursor to last id in this batch
            lastId = batchData[batchData.length - 1].id;

            if (batchData.length < batchSize) {
                console.log('  Last batch received');
                break;
            }
        }

        console.log(`📊 getLeads returned ${allLeads.length} leads total`);
        // ── Cache store ──────────────────────────────────────────────────────
        cacheSet('leads', allLeads);
        // ────────────────────────────────────────────────────────────────────
        return allLeads;
    }

    /**
     * Get leads changed since a given timestamp (for delta polling)
     * @param {string} since - ISO timestamp to fetch changes since
     * @param {Object} filters - Optional filters (assignedto, etc.)
     * @returns {Array} leads updated since the given timestamp
     */
    async getLeadsSince(since, filters = {}) {
        const supabase = getSupabase();
        let query = supabase.from('leads').select(LEADS_LIST_COLUMNS)
            .eq('archived', false)
            .gte('updated_at', since)
            .order('id', { ascending: true });

        if (filters.assignedto) {
            const values = Array.isArray(filters.assignedto) ? filters.assignedto : [filters.assignedto];
            const orFilter = values
                .map(v => `assignedto.ilike."${String(v).replace(/"/g, '')}"`)
                .join(',');
            query = query.or(orFilter);
        }

        const { data, error } = await query;
        if (error) {
            console.error('Error fetching delta leads:', error);
            throw error;
        }
        const changed = data || [];
        // Merge delta changes into the cache so it stays fresh
        if (changed.length > 0) {
            const existing = cacheGet('leads');
            if (existing) {
                const changedIds = new Set(changed.map(l => l.id));
                const merged = [...existing.filter(l => !changedIds.has(l.id)), ...changed];
                cacheSet('leads', merged);
            }
        }
        return changed;
    }

    // Reads the full row, including the heavy JSON columns the list query omits.
    // Guarded like the list paths: once the Supabase session lapses the client
    // silently falls back to `anon`, RLS returns zero rows, and .single() used to
    // surface that as a raw PostgREST coercion error while the modal showed empty
    // notes — the lead looked wiped when nothing had been touched.
    async getLeadById(id) {
        await assertSignedIn();
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('leads')
            .select('*')
            .eq('id', id)
            .maybeSingle();
        if (error) throw error;
        if (!data) throw new LeadNotVisibleError(id);
        return data;
    }

    // tasks is excluded from LEADS_LIST_COLUMNS; this returns only leads that have any.
    async getLeadsWithTasks() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('leads')
            .select('id,firstname,lastname,primaryphone,primaryemail,assignedto,team_id,tasks')
            .not('tasks', 'is', null);
        if (error) throw error;
        return (data || []).filter(l => Array.isArray(l.tasks) && l.tasks.length > 0);
    }

    // `options.allowNoteRemoval` is for the one caller that legitimately removes a
    // note — the Delete button on a note the user wrote.  Every other write goes
    // through guardNotes, which cannot lose one.
    async updateLead(leadData, options = {}) {
        const supabase = getSupabase();

        // CRITICAL FIX: Remove undefined values and protect assignedto field
        // If assignedto is undefined or null, fetch current value and preserve it
        const cleanedData = { ...leadData };

        // Always stamp updated_at so dashboard "Updates Today" counter works
        cleanedData.updated_at = new Date().toISOString();
        // Record who acted so the audit trail names a person, not 'system'.
        // Always overwrite: callers that spread a fetched row carry a stale
        // updated_by, which would credit the previous editor for this change.
        const actor = currentUsername();
        if (actor) cleanedData.updated_by = actor;

        // Remove undefined values to prevent accidentally nulling fields
        Object.keys(cleanedData).forEach(key => {
            if (cleanedData[key] === undefined) {
                delete cleanedData[key];
            }
        });

        // CRITICAL: If assignedto is being set to null/undefined, remove it from update
        // This prevents agents from accidentally clearing their own assignment
        if (cleanedData.assignedto === null || cleanedData.assignedto === '') {
            console.warn('⚠️ Prevented assignedto from being set to null/empty. Removing from update.');
            delete cleanedData.assignedto;
        }

        if (!options.allowNoteRemoval) {
            await guardNotes(supabase, leadData.id, cleanedData);
        }

        const { data, error } = await supabase
            .from('leads')
            .update(cleanedData)
            .eq('id', leadData.id);

        if (error) {
            console.error('Supabase error updating lead:', error);
            throw error;
        }
        // Patch the single record in cache — no full re-fetch needed
        cachePatch('leads', cleanedData);
        return data;
    }

    // ── Soft delete / archive ─────────────────────────────────────────────────

    async archiveLead(leadId, archivedBy) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('leads')
            .update({ archived: true, archived_by: archivedBy, archived_at: new Date().toISOString(), updated_at: new Date().toISOString() })
            .eq('id', leadId);
        if (error) throw error;
        cacheInvalidate('leads');
    }

    async restoreLead(leadId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('leads')
            .update({ archived: false, archived_by: null, archived_at: null, updated_at: new Date().toISOString() })
            .eq('id', leadId);
        if (error) throw error;
        cacheInvalidate('leads');
    }

    // ── Concurrency / editing lock ────────────────────────────────────────────

    async markLeadEditing(leadId, username) {
        const supabase = getSupabase();
        await supabase.from('leads')
            .update({ editing_by: username, editing_at: new Date().toISOString() })
            .eq('id', leadId);
    }

    async clearLeadEditing(leadId, username) {
        const supabase = getSupabase();
        // Only clear if WE are the one editing (don't clear someone else's lock)
        await supabase.from('leads')
            .update({ editing_by: null, editing_at: null })
            .eq('id', leadId)
            .eq('editing_by', username);
    }

    // ── Fingerprint helpers ───────────────────────────────────────────────────
    // Build a normalised fingerprint row from a lead object
    _buildFingerprint(lead, deletedBy) {
        const emailNorm = (lead.primaryemail || '').toLowerCase().trim();
        const firstNorm = (lead.firstname || '').toLowerCase().trim();
        const lastNorm  = (lead.lastname  || '').toLowerCase().trim();
        const fullNorm  = `${firstNorm} ${lastNorm}`.trim();
        // Strip non-digits for loose phone matching
        const phoneNorm = (lead.primaryphone || '').replace(/\D/g, '');
        return {
            original_lead_id:  lead.id || null,
            email_normalized:  emailNorm  || null,
            fullname_normalized: fullNorm || null,
            phone_normalized:  phoneNorm  || null,
            firstname:         lead.firstname     || null,
            lastname:          lead.lastname      || null,
            primaryemail:      lead.primaryemail  || null,
            primaryphone:      lead.primaryphone  || null,
            status_at_deletion: lead.status       || null,
            leadsource:        lead.leadsource    || null,
            assignedto:        lead.assignedto    || null,
            country:           lead.country       || null,
            deleted_by:        deletedBy          || null,
        };
    }

    // Save fingerprint(s) for an array of lead objects before deletion
    async _saveFingerprints(leads, deletedBy) {
        if (!leads || leads.length === 0) return;
        const supabase = getSupabase();
        const rows = leads.map(l => this._buildFingerprint(l, deletedBy));
        const { error } = await supabase.from('lead_fingerprints').insert(rows);
        if (error) {
            // Non-fatal: log but don't block the deletion
            console.warn('⚠️ Could not save lead fingerprints:', error.message);
        }
    }

    // ── Activity report ───────────────────────────────────────────────────────
    // Who did what over a window: status/assignment edits, deletions, and any
    // note/task history that was trimmed.
    async getActivityReport(fromISO, toISO) {
        const supabase = getSupabase();
        const [edits, deletions, trimmed] = await Promise.all([
            supabase.from('lead_edits')
                .select('lead_id,username,field_name,old_value,new_value,edited_at')
                .gte('edited_at', fromISO).lt('edited_at', toISO)
                .order('edited_at', { ascending: false }).limit(10000),
            supabase.from('leads_deleted_archive')
                .select('lead_id,deleted_by,deleted_at,row_data')
                .gte('deleted_at', fromISO).lt('deleted_at', toISO)
                .order('deleted_at', { ascending: false }).limit(5000),
            supabase.from('leads_json_history')
                .select('lead_id,changed_by,column_name,changed_at,old_value')
                .gte('changed_at', fromISO).lt('changed_at', toISO)
                .order('changed_at', { ascending: false }).limit(5000),
        ]);
        if (edits.error) throw edits.error;
        return {
            edits: edits.data || [],
            deletions: deletions.data || [],
            trimmed: trimmed.data || [],
        };
    }

    // Fetch all stored fingerprints (for duplicate checking)
    async getLeadFingerprints() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('lead_fingerprints')
            .select('*')
            .order('deleted_at', { ascending: false });
        if (error) {
            console.warn('⚠️ Could not load lead fingerprints:', error.message);
            return [];
        }
        return data || [];
    }
    // ─────────────────────────────────────────────────────────────────────────

    async deleteLead(leadId, deletedBy) {
        const supabase = getSupabase();
        const actor = deletedBy || currentUsername();

        // 1. Fetch the lead so we can fingerprint it
        const { data: leadData } = await supabase
            .from('leads')
            .select('*')
            .eq('id', leadId)
            .single();

        // 2. Save fingerprint (best-effort)
        if (leadData) await this._saveFingerprints([leadData], actor);

        // 3. Stamp the actor so the delete archive records who removed it
        if (actor) await supabase.from('leads').update({ updated_by: actor }).eq('id', leadId);

        // 4. Delete
        const { error } = await supabase
            .from('leads')
            .delete()
            .eq('id', leadId);

        if (error) throw error;
        cacheInvalidate('leads');
    }

    async deleteLeads(leadIds, deletedBy) {
        if (!leadIds || leadIds.length === 0) return;
        const supabase = getSupabase();
        const actor = deletedBy || currentUsername();

        // 1. Fetch all leads so we can fingerprint them
        const { data: leadsData } = await supabase
            .from('leads')
            .select('*')
            .in('id', leadIds);

        // 2. Save fingerprints (best-effort)
        if (leadsData && leadsData.length > 0) {
            await this._saveFingerprints(leadsData, actor);
        }

        // 3. Stamp the actor so the delete archive records who removed them
        if (actor) await supabase.from('leads').update({ updated_by: actor }).in('id', leadIds);

        // 4. Delete
        const { error } = await supabase
            .from('leads')
            .delete()
            .in('id', leadIds);
        if (error) throw error;
        cacheInvalidate('leads');
    }

    async clearLeads(actingUser) {
        if (!actingUser || actingUser.role !== 'superadmin') {
            throw new Error('Only a superadmin can clear all leads.');
        }
        const supabase = getSupabase();

        // Fingerprint everything before the hard delete, same safety net
        // deleteLead/deleteLeads already get - this used to skip it entirely,
        // which is why the last accidental clear was unrecoverable.
        const allLeads = await this.getLeads({ _skipCache: true });
        if (allLeads.length > 0) {
            await this._saveFingerprints(allLeads, actingUser.username);
        }

        const { error } = await supabase
            .from('leads')
            .delete()
            .neq('id', 0);

        if (error) throw error;
        cacheInvalidate('leads');
    }

    // Users
    async getUsers() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('crm_users')
            .select(CRM_USER_COLUMNS);

        if (error) throw error;
        return data || [];
    }

    // Teams - only superadmin ever needs this list (everyone else is
    // locked to their own team server-side regardless of what's shown)
    async getTeams() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('teams')
            .select('*')
            .order('name');

        if (error) throw error;
        return data || [];
    }

    async addUser(userData) {
        return callAdminRpc('create', {
            p_username: userData.username,
            p_password: userData.password,
            p_first_name: userData.first_name,
            p_last_name: userData.last_name,
            p_role: userData.role,
            p_team_id: userData.team_id || null,
        });
    }

    async setUserRoleAndTeam(username, { role, team_id } = {}) {
        return callAdminRpc('update', {
            p_username: username,
            p_role: role || null,
            p_team_id: team_id || null,
        });
    }

    async setUserPassword(username, password) {
        return callAdminRpc('set-password', { p_username: username, p_password: password });
    }

    async deleteUser(username) {
        return callAdminRpc('delete', { p_username: username });
    }

    async updateUser(username, updates) {
        const supabase = getSupabase();

        const { role, team_id, ...plainUpdates } = updates;
        if (role !== undefined || team_id !== undefined) {
            await callAdminRpc('update', {
                p_username: username,
                p_role: role || null,
                p_team_id: team_id || null,
            });
        }

        if (Object.keys(plainUpdates).length > 0) {
            const { error } = await supabase
                .from('crm_users')
                .update(plainUpdates)
                .eq('username', username);
            if (error) throw error;
        }
    }

    async bulkUpdateLeads(leadIds, updates) {
        try {
            const supabase = getSupabase();
            const updatePromises = leadIds.map(leadId =>
                supabase
                    .from('leads')
                    .update(updates)
                    .eq('id', leadId)
            );

            await Promise.all(updatePromises);
            return { success: true };
        } catch (error) {
            console.error('Error bulk updating leads:', error);
            throw error;
        }
    }

    async transferLeads(leadIds, newAgent) {
        try {
            const supabase = getSupabase();
            const { data, error } = await supabase
                .from('leads')
                .update({ assignedto: newAgent })
                .in('id', leadIds);
            
            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error transferring leads:', error);
            throw error;
        }
    }
   
    // Sessions
    async createSession(sessionData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('sessions')
            .upsert(sessionData, { onConflict: 'session_id' });
        
        if (error) throw error;
        return data;
    }

    async getSession(sessionId) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('sessions')
            .select('*')
            .eq('session_id', sessionId)
            .single();
        
        if (error && error.code !== 'PGRST116') throw error;
        return data;
    }

    async deleteSession(sessionId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('sessions')
            .delete()
            .eq('session_id', sessionId);
        
        if (error) throw error;
    }

    // Groups
    async getGroups() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('groups')
            .select('*');
        
        if (error) throw error;
        return data || [];
    }

    async addGroup(groupData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('groups')
            .insert(groupData);
        
        if (error) throw error;
        return data;
    }

    async deleteGroup(groupId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('groups')
            .delete()
            .eq('id', groupId);
        
        if (error) throw error;
    }

    // Documents
    async getDocuments() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('documents')
            .select('*')
            .order('uploaded_at', { ascending: false });
        
        if (error) throw error;
        return data || [];
    }

    async uploadFileToStorage(file, folder = 'documents') {
        const supabase = getSupabase();
        const fileExt = file.name.split('.').pop();
        const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

        const { data, error } = await supabase.storage
            .from('documents')
            .upload(fileName, file);

        if (error) throw error;

        // Get public URL
        const { data: urlData } = supabase.storage
            .from('documents')
            .getPublicUrl(fileName);

        return urlData.publicUrl;
    }

    async addDocument(docData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('documents')
            .insert(docData);

        if (error) throw error;
        return data;
    }

    async deleteDocument(docId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('documents')
            .delete()
            .eq('id', docId);
        
        if (error) throw error;
    }
    
    async updateDocument(docData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('documents')
            .update(docData)
            .eq('id', docData.id);
        
        if (error) throw error;
        return data;
    }

    // Buy Orders
    async getBuyOrders() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('buyorders')
            .select('*')
            .order('created_at', { ascending: false });
        
        if (error) throw error;
        return data || [];
    }

    async addBuyOrder(orderData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('buyorders')
            .insert(orderData)
            .select();
        
        if (error) throw error;
        return data;
    }

    async updateBuyOrder(orderId, updates) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('buyorders')
            .update(updates)
            .eq('id', orderId)
            .select();
        
        if (error) throw error;
        return data;
    }

    async deleteBuyOrder(orderId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('buyorders')
            .delete()
            .eq('id', orderId);
        
        if (error) throw error;
    }

    // Callbacks
    async getCallbacks() {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('callbacks')
            .select('*')
            .order('callback_date', { ascending: true });
        
        if (error) throw error;
        return data || [];
    }

    async addCallback(callbackData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('callbacks')
            .insert(callbackData)
            .select();
        
        if (error) throw error;
        return data;
    }

    async updateCallback(callbackId, updates) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('callbacks')
            .update(updates)
            .eq('id', callbackId)
            .select();
        
        if (error) throw error;
        return data;
    }

    async deleteCallback(callbackId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('callbacks')
            .delete()
            .eq('id', callbackId);
        
        if (error) throw error;
    }

    // Messages (Chat)
    async getMessages(user1, user2) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('messages')
            .select('*')
            .or(`and(sender.eq.${user1},receiver.eq.${user2}),and(sender.eq.${user2},receiver.eq.${user1})`)
            .order('created_at', { ascending: true });

        if (error) throw error;
        return data || [];
    }

    async sendMessage(messageData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('messages')
            .insert(messageData)
            .select();

        if (error) throw error;
        return data;
    }

    async markMessagesAsRead(sender, receiver) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('messages')
            .update({ status: 'read' })
            .eq('sender', sender)
            .eq('receiver', receiver)
            .neq('status', 'read');

        if (error) throw error;
        return data;
    }

    async markMessagesAsDelivered(sender, receiver) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('messages')
            .update({ status: 'delivered' })
            .eq('sender', sender)
            .eq('receiver', receiver)
            .eq('status', 'sent');

        if (error) throw error;
        return data;
    }

    async getConversationPartners(username) {
        const supabase = getSupabase();
        // Get distinct users who have sent a message TO this user (initiated conversation)
        const { data, error } = await supabase
            .from('messages')
            .select('sender')
            .eq('receiver', username)
            .is('group_id', null);

        if (error) throw error;

        const partners = new Set();
        (data || []).forEach(msg => partners.add(msg.sender));
        return [...partners];
    }

    async getUnreadCounts(username) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('messages')
            .select('sender')
            .eq('receiver', username)
            .neq('status', 'read');

        if (error) throw error;

        // Count unread per sender
        const counts = {};
        (data || []).forEach(msg => {
            counts[msg.sender] = (counts[msg.sender] || 0) + 1;
        });
        return counts;
    }

    // Chat Groups
    async getChatGroups(username) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('chat_groups')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        // Filter client-side to avoid text[] vs jsonb type mismatch with .contains()
        return (data || []).filter(g => Array.isArray(g.members) && g.members.includes(username));
    }

    async createChatGroup(groupData) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('chat_groups')
            .insert(groupData)
            .select();

        if (error) throw error;
        return data;
    }

    async updateChatGroup(groupId, updates) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('chat_groups')
            .update(updates)
            .eq('id', groupId)
            .select();

        if (error) throw error;
        return data;
    }

    async deleteChatGroup(groupId) {
        const supabase = getSupabase();
        const { error } = await supabase
            .from('chat_groups')
            .delete()
            .eq('id', groupId);

        if (error) throw error;
    }

    // Group Messages
    async getGroupMessages(groupId) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('messages')
            .select('*')
            .eq('group_id', groupId)
            .order('created_at', { ascending: true });

        if (error) throw error;
        return data || [];
    }

    async getUnreadGroupCounts(username) {
        const supabase = getSupabase();
        // Fetch all groups client-side (avoids jsonb .contains() error)
        const { data: allGroups, error: gErr } = await supabase
            .from('chat_groups')
            .select('id, members');

        if (gErr) throw gErr;
        const groups = (allGroups || []).filter(g => Array.isArray(g.members) && g.members.includes(username));
        if (groups.length === 0) return {};

        const groupIds = groups.map(g => g.id);
        // Fetch all group messages not sent by this user, filter read_by client-side
        const { data, error } = await supabase
            .from('messages')
            .select('group_id, read_by, sender')
            .in('group_id', groupIds)
            .neq('sender', username);

        if (error) throw error;

        const counts = {};
        (data || []).forEach(msg => {
            const readBy = Array.isArray(msg.read_by) ? msg.read_by : [];
            if (!readBy.includes(username)) {
                counts[msg.group_id] = (counts[msg.group_id] || 0) + 1;
            }
        });
        return counts;
    }

    async markGroupMessagesAsRead(groupId, username) {
        const supabase = getSupabase();
        // Fetch all messages in group not sent by user, filter read_by client-side
        const { data: allMsgs, error: fetchErr } = await supabase
            .from('messages')
            .select('id, read_by')
            .eq('group_id', groupId)
            .neq('sender', username);

        if (fetchErr) throw fetchErr;
        if (!allMsgs || allMsgs.length === 0) return;

        const unread = allMsgs.filter(msg => {
            const readBy = Array.isArray(msg.read_by) ? msg.read_by : [];
            return !readBy.includes(username);
        });
        if (unread.length === 0) return;

        for (const msg of unread) {
            const readBy = Array.isArray(msg.read_by) ? [...msg.read_by, username] : [username];
            await supabase.from('messages').update({ read_by: readBy }).eq('id', msg.id);
        }
    }

    async getLatestUnreadMessages(username) {
        const supabase = getSupabase();

        // Latest unread DM per sender
        const { data: dmData, error: dmError } = await supabase
            .from('messages')
            .select('id, sender, receiver, content, created_at, file_url, file_name')
            .eq('receiver', username)
            .neq('status', 'read')
            .is('group_id', null)
            .order('created_at', { ascending: false });

        if (dmError) throw dmError;

        const latestDMs = {};
        (dmData || []).forEach(msg => {
            if (!latestDMs[msg.sender]) latestDMs[msg.sender] = msg;
        });

        // Latest unread group message per group
        const { data: allGroups, error: gErr } = await supabase
            .from('chat_groups')
            .select('id, name, members');

        if (gErr) throw gErr;
        const myGroups = (allGroups || []).filter(g => Array.isArray(g.members) && g.members.includes(username));

        const latestGroups = {};
        if (myGroups.length > 0) {
            const groupIds = myGroups.map(g => g.id);
            const { data: gMsgs, error: gmErr } = await supabase
                .from('messages')
                .select('id, group_id, sender, content, created_at, file_url, file_name, read_by')
                .in('group_id', groupIds)
                .neq('sender', username)
                .order('created_at', { ascending: false });

            if (!gmErr) {
                const groupMap = {};
                myGroups.forEach(g => { groupMap[g.id] = g.name; });

                (gMsgs || []).forEach(msg => {
                    const readBy = Array.isArray(msg.read_by) ? msg.read_by : [];
                    if (!readBy.includes(username) && !latestGroups[msg.group_id]) {
                        latestGroups[msg.group_id] = { ...msg, group_name: groupMap[msg.group_id] };
                    }
                });
            }
        }

        return {
            dms: Object.values(latestDMs),
            groups: Object.values(latestGroups),
        };
    }

    async uploadChatFile(file) {
        const supabase = getSupabase();
        const fileExt = file.name.split('.').pop();
        const fileName = `chat/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

        const { data, error } = await supabase.storage
            .from('chat-files')
            .upload(fileName, file);

        if (error) throw error;

        const { data: urlData } = supabase.storage
            .from('chat-files')
            .getPublicUrl(fileName);

        return urlData.publicUrl;
    }

    // Groups - add missing updateGroup method
    async updateGroup(groupId, updates) {
        const supabase = getSupabase();
        const { data, error } = await supabase
            .from('groups')
            .update(updates)
            .eq('id', groupId)
            .select();
        
        if (error) throw error;
        return data;
    }
}

export const crmService = new CRMService();
